import { useState } from 'react';
import { ChaseRow, chaseKind, type ChaseKind } from './ChaseRow.tsx';
import type { Tenant } from './api.ts';
import type { AwaitingRow } from './clientViews.ts';

/**
 * The Awaiting consent tab: every client who needs a consent link, and the
 * bookkeeping around sending it.
 *
 * This was a screen of its own — Onboarding — and the reason it is a tab is
 * that it was never a different list. It held the same clients the estate held,
 * filtered to the ones that had not connected, and an operator deciding which
 * of the two to open had to know in advance what they were about to find. The
 * clients are one list; this is the part of it with work outstanding.
 *
 * **Membership is a connection state, never the chase payload.** The server's
 * chase list answers a narrower question — who to nudge *next* — and drops
 * anyone chased in the last week or already reminded four times. The join in
 * `clientViews.ts` carries the full argument; what matters here is that a row
 * with no chase entry is an ordinary row.
 *
 * Nothing on this tab is a verdict, so nothing on it is red. A client waiting
 * on consent and a withdrawn consent are both chores; borrowing the failure
 * colour for either would blunt the one place in the product where red has to
 * mean something.
 */

type Filter = 'all' | ChaseKind;

interface AwaitingConsentProps {
  readonly rows: readonly AwaitingRow[];
  /**
   * How many of the waiting clients are overdue a chase — seven days since the
   * link or the last reminder. The server's figure, because the rule is the
   * server's and a second copy of it here would be free to disagree.
   */
  readonly needsChasing: number;
  readonly entraConfigured: boolean;
  readonly onPatch: (id: string, patch: Partial<Tenant>) => void;
  readonly onStateChanged: () => void;
  readonly announce: (text: string) => void;
}

export const AwaitingConsent = ({
  rows,
  needsChasing,
  entraConfigured,
  onPatch,
  onStateChanged,
  announce,
}: AwaitingConsentProps) => {
  const [filter, setFilter] = useState<Filter>('all');

  const visible = rows.filter((row) => filter === 'all' || chaseKind(row.tenant) === filter);

  return (
    <div className="cl-awaiting">
      {/* The trust statement, in full and where an operator with clients will
          read it: an MSP is about to hand this product their entire client
          list, and "will it email them?" is the first question they ask.
          Answering it in a footnote answers it for nobody. */}
      <div className="callout ob-notice ob-promise">
        <span className="callout-title">DriftWatch never emails your clients — you do</span>
        Consent links are produced here and copied by you. They go out from your own mailbox, under
        your own signature and domain, because a request for access to a client&rsquo;s tenant
        should arrive from the people they already work with. Nothing on this screen sends anything.
      </div>

      {rows.length === 0 ? (
        <NobodyAwaiting />
      ) : (
        <>
          <ChaseSummary
            rows={rows}
            needsChasing={needsChasing}
            filter={filter}
            onFilter={(next) => setFilter((current) => (current === next ? 'all' : next))}
          />

          <section className="ob-chase" aria-labelledby="cl-chase-heading">
            <h2 id="cl-chase-heading">Chase list</h2>
            <p className="note ob-chase-note">
              Longest waiting first. Copying a link and recording that you sent it is one button,
              because you will do it once per client and then again next week.
            </p>

            {filter !== 'all' && (
              <div className="toolbar ob-filter">
                <p className="note">
                  Showing <span className="num">{visible.length}</span> of{' '}
                  <span className="num">{rows.length}</span>.
                </p>
                <button
                  type="button"
                  className="btn btn-quiet btn-sm"
                  onClick={() => setFilter('all')}
                >
                  Show all
                </button>
              </div>
            )}

            {visible.length === 0 ? (
              <div className="empty">
                <span className="empty-title">Nothing in this group</span>
                <p className="empty-body">
                  No client is in this state at the moment. Clear the filter to see everybody
                  waiting.
                </p>
              </div>
            ) : (
              <div className="tbl-wrap">
                <table className="tbl ob-tbl">
                  <thead>
                    <tr>
                      <th>Customer</th>
                      <th>Contact</th>
                      <th>Waiting</th>
                      <th>Reminders</th>
                      <th>Send the link</th>
                    </tr>
                  </thead>
                  {visible.map((row) => (
                    <ChaseRow
                      key={row.tenant.id}
                      row={row}
                      entraConfigured={entraConfigured}
                      onPatch={onPatch}
                      onStateChanged={onStateChanged}
                      announce={announce}
                    />
                  ))}
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
};

interface ChaseSummaryProps {
  readonly rows: readonly AwaitingRow[];
  readonly needsChasing: number;
  readonly filter: Filter;
  readonly onFilter: (filter: ChaseKind) => void;
}

/**
 * The only split that changes what an operator does next, and the reason this
 * tab has figures of its own at all.
 *
 * "Not connected" as a single number tells you there is a problem without
 * telling you whose it is: a client nobody has written to yet is the MSP's own
 * work, and a client who has had the link and not acted on it is the customer's.
 * The Live tab's five tiles describe the estate and cannot carry this, because
 * it is a distinction only these rows have.
 *
 * Every figure is counted from the rows underneath rather than read from the
 * server's summary, which counts every non-archived tenant and would put a
 * number above a table that does not contain it. The one exception is
 * `needsChasing`: it is a seven-day rule applied to `lastRemindedAt`, the server
 * owns it, and it is a hint rather than a filter.
 *
 * Counts only — there is no percentage connected, and there is not going to be
 * one. Thirty-one of forty is a list somebody can work through; 78% is a number
 * to chase.
 */
const ChaseSummary = ({ rows, needsChasing, filter, onFilter }: ChaseSummaryProps) => {
  const count = (kind: ChaseKind) => rows.filter((row) => chaseKind(row.tenant) === kind).length;
  const notSent = count('not-sent');
  const waiting = count('waiting');
  const attention = count('attention');

  return (
    <section className="card ob-summary" aria-labelledby="cl-chase-summary-heading">
      <div className="card-head">
        <h2 id="cl-chase-summary-heading">Whose move it is</h2>
        <p className="note">
          <span className="num">{rows.length}</span>{' '}
          {rows.length === 1 ? 'client needs' : 'clients need'} a consent link
        </p>
      </div>

      <div className="card-body">
        <div className="stat-grid">
          <button
            type="button"
            className="stat ob-stat"
            aria-pressed={filter === 'not-sent'}
            onClick={() => onFilter('not-sent')}
          >
            <span className="stat-figure num">{notSent}</span>
            <span className="stat-label">Not sent yet</span>
            <span className="stat-hint">Yours to do — nobody has been asked.</span>
          </button>

          <button
            type="button"
            className="stat ob-stat"
            aria-pressed={filter === 'waiting'}
            onClick={() => onFilter('waiting')}
          >
            <span className="stat-figure num">{waiting}</span>
            <span className="stat-label">Waiting on the customer</span>
            <span className="stat-hint">
              Link sent, not yet clicked
              {needsChasing > 0 ? ` — ${needsChasing} due a chase.` : '.'}
            </span>
          </button>

          <button
            type="button"
            className="stat ob-stat"
            aria-pressed={filter === 'attention'}
            onClick={() => onFilter('attention')}
          >
            <span className="stat-figure num">{attention}</span>
            <span className="stat-label">Consent withdrawn</span>
            <span className="stat-hint">Connected once, and no longer. They need asking again.</span>
          </button>
        </div>

        <p className="note ob-stat-note">
          Each one narrows the list below. Selecting the one already selected clears it.
        </p>
      </div>
    </section>
  );
};

/**
 * The Awaiting consent tab, once everybody has answered.
 *
 * It is a good outcome rather than a missing one, and it needs saying: a table
 * showing its headers and nothing else is indistinguishable from a table whose
 * rows failed to arrive. No count here — the tab label beside it already
 * carries the zero.
 */
const NobodyAwaiting = () => (
  <div className="empty cl-empty">
    <span className="empty-title">Nobody is awaiting consent</span>
    <p className="empty-body">
      Every client this deployment holds has granted consent and kept it, or been archived. A client
      appears here when they are added and again if their consent is ever withdrawn — until their
      Global Administrator opens the consent link and grants it. DriftWatch never emails your
      clients, you do.
    </p>
  </div>
);
